import DataViewQueryResultTable from "./queryRunner/DataViewQueryResultTable";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useQueryRunResults } from "context/QueryRunContext";
import { motion } from "framer-motion";
import _ from "lodash";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

/**
 * Handles ID column selection from either the table header or ID column modal
 * Sets the 'idColumns' form field
 */
export const handleIdColumnSelect = (key: string, selectedIdColumns: string[], onChange: (event: string[]) => void) => {
  const newSelectedIdColumns = new Set(selectedIdColumns);
  if (newSelectedIdColumns.has(key)) {
    newSelectedIdColumns.delete(key);
  } else {
    newSelectedIdColumns.add(key);
  }
  onChange(Array.from(newSelectedIdColumns));
};

interface Props {
  prefix?: string;
  previewOpen: boolean;
  setPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readOnly?: boolean;
  transparentBackground?: boolean;
  className?: string;
  previewColumnsSelectable?: boolean;
}

function onPreviewHandleMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
  e.preventDefault();
  e.stopPropagation();
  const previewResizer = (e: MouseEvent) => {
    const previewContainer: any = document.querySelector("[data-preview-container]");
    if (!previewContainer) return;
    // Get the first element with the data-preview-resizer attribute
    const parent = document.querySelector("[data-preview-resizer]");
    if (!parent) return;
    // Calculate the new top position of the preview container relative to the parent
    let newTop = e.clientY - parent.getBoundingClientRect().top;
    // Set the new top position of the preview container
    // Clamp the new top position to 10% and 90% of the parent height
    newTop = Math.max(0.1 * parent.clientHeight, Math.min(newTop, 0.75 * parent.clientHeight));
    previewContainer.style.top = `${newTop}px`;
  };

  const cleanUp = () => {
    window.removeEventListener("mousemove", previewResizer);
    setTimeout(() => window.removeEventListener("mouseup", cleanUp));
  };

  window.addEventListener("mousemove", previewResizer);
  window.addEventListener("mouseup", cleanUp);
}

export default function QueryPreviews({
  previewOpen,
  setPreviewOpen,
  readOnly = false,
  transparentBackground,
  className,
  previewColumnsSelectable = true,
  prefix = "",
}: Props) {
  const fieldPrefix = prefix ? `${prefix}.` : "";
  const { queryRunResults, queryRunMetaInfo, setQueryRunResults } = useQueryRunResults();
  const { totalNumRows: queryNumRows, dataviewQueryDurationMs: queryDurationMs, error: queryError } = queryRunMetaInfo ?? {};

  const { control, watch } = useFormContext();
  const databaseId = watch(`${fieldPrefix}databaseId`);

  // If database id changes, we want to clear the results
  useEffect(() => {
    if (databaseId) {
      setQueryRunResults(undefined);
    }
  }, [databaseId]);

  if (!queryRunResults) return null;

  return (
    <div className={`pt-1 h-16 ${transparentBackground ? "" : "bg-white"}`}>
      <motion.div
        layout
        data-preview-container
        style={{
          top: previewOpen ? "10%" : "",
        }}
        className={`${
          className ? className : "bottom-[4rem] left-0 right-0"
        } absolute bg-white rounded-md shadow-[0px_-2px_10px_rgb(0,0,0,0.05)] z-40 overflow-hidden flex flex-col`}
      >
        <motion.div layout="position" className="h-16">
          {previewOpen ? (
            <div className="w-full flex items-center justify-center cursor-ns-resize py-2" onMouseDown={onPreviewHandleMouseDown}>
              <div className="w-5 border-2 border-gray-200" />
            </div>
          ) : null}
          <div className="flex cursor-pointer py-5 aria-expanded:pt-0 px-2" onClick={() => setPreviewOpen((prev) => !prev)} aria-expanded={previewOpen}>
            <p className="text-lg font-medium flex-1">Preview</p>
            <ChevronDownIcon className={`w-5 h-5 ml-2 inline-block transition-transform ${!previewOpen ? "rotate-180" : ""}`} />
          </div>
        </motion.div>
        <motion.div layout className={`relative w-full overflow-hidden flex-1 ${!previewOpen ? "max-h-0" : ""} `}>
          {queryRunResults.length > 0 ? (
            <Controller
              control={control}
              name={`${fieldPrefix}idColumns`}
              defaultValue={[]}
              render={({ field: { onChange: onChange, value: selectedIdColumns } }) => (
                <DataViewQueryResultTable
                  results={queryRunResults}
                  onChange={onChange}
                  selectedIdColumns={selectedIdColumns}
                  handleIdColumnSelect={handleIdColumnSelect}
                  dataviewQueryDurationMs={queryDurationMs}
                  totalNumRows={queryNumRows}
                  columnsSelectable={previewColumnsSelectable}
                  readOnly={readOnly}
                />
              )}
            />
          ) : null}
          {queryError ? (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center">
              <h3 className="capitalize text-left font-medium text-xl text-red-500 w-[80%]">Query Errors</h3>
              {databaseId ? (
                <>
                  <span className="capitalize text-left text-red-500 w-[80%] whitespace-pre-wrap">{queryError}</span>
                </>
              ) : (
                <span className="capitalize text-left text-red-500 w-[80%] whitespace-pre-wrap">Select a Database to run preview queries.</span>
              )}
            </div>
          ) : null}
        </motion.div>
      </motion.div>
    </div>
  );
}
